/**
 * Undocumented-edge geometry — the "telemetry-only" half of conformance drift.
 *
 * The edge-health layer styles MODELED edges (red / dim-dashed). But a call the
 * model doesn't document (e.g. catalog → inventory) has no board edge and no
 * planned route, so nothing draws it. This module finds those observed-but-
 * unmodeled edges and resolves each endpoint to a board node, so a layer can
 * draw a violation arc between the two node rects.
 *
 * Keying mirrors the rest of telemetry: modeled edges reduce to their top-level
 * module pair via {@link edgeServicePair}, observed edges are keyed by
 * `trafficEdgeKey`, and nodes index by `normServiceName(id)` — so a module id
 * ("Catalog") and its OTel service ("catalog") resolve to the same node.
 */
import type { BoardEdge, BoardItem } from "@archlang/scene/board";
import { normServiceName, trafficEdgeKey } from "../liveTrafficStore.js";
import type { EdgeStat } from "../liveStatsStore.js";
import { edgeServicePair } from "../edgeServicePair.js";

/** The set of modeled edge keys (normalized service pairs) on the board. */
export function modeledKeySet(edges: readonly BoardEdge[]): Set<string> {
  const set = new Set<string>();
  for (const e of edges) {
    const pair = edgeServicePair(e);
    if (pair) set.add(trafficEdgeKey(pair[0], pair[1]));
  }
  return set;
}

/** An observed edge with no modeling — both endpoints resolved to board nodes. */
export interface UndocumentedEdge {
  readonly key: string;
  readonly from: BoardItem;
  readonly to: BoardItem;
  readonly perSec: number;
  readonly errorRate?: number;
}

function endpointsOf(stat: EdgeStat): readonly [string, string] | null {
  // The live feed always carries the endpoint labels (post service→module
  // remap); we key nodes off those rather than splitting the opaque store key.
  if (stat.from && stat.to) return [normServiceName(stat.from), normServiceName(stat.to)];
  return null;
}

/**
 * Observed edges that aren't modeled AND whose BOTH endpoints are nodes on this
 * space (so they're drawable between two rects). Cross-space or unknown-node
 * edges are skipped — they'd need a virtual node, out of scope here.
 */
export function undocumentedObserved(
  edges: readonly BoardEdge[],
  stats: ReadonlyMap<string, EdgeStat>,
  nodes: ReadonlyMap<string, BoardItem>,
): UndocumentedEdge[] {
  if (stats.size === 0) return [];
  const modeled = modeledKeySet(edges);
  const index = new Map<string, BoardItem>();
  for (const [id, item] of nodes) index.set(normServiceName(id), item);

  const out: UndocumentedEdge[] = [];
  for (const [key, stat] of stats) {
    if (modeled.has(key) || !(stat.perSec > 0)) continue;
    const ep = endpointsOf(stat);
    if (!ep) continue;
    const from = index.get(ep[0]);
    const to = index.get(ep[1]);
    if (!from || !to || from === to) continue;
    out.push({ key, from, to, perSec: stat.perSec, errorRate: stat.errorRate });
  }
  return out;
}

/** A resolved undocumented pair — both endpoints mapped to model module keys.
 *  The drift TOPOLOGY unit: the layout-affecting shape, metric VALUES excluded. */
export interface DriftPair {
  readonly from: string;
  readonly to: string;
}

/**
 * The drift TOPOLOGY — observed edges that aren't modeled, each resolved to a
 * pair of MODEL module keys. This is the set folded into the board build as
 * first-class `violation` edges; keeping it metric-free (only presence, via
 * `perSec > 0`) means the board re-solves when the drift SHAPE changes (a new
 * unexpected call / a drift toggle) but NOT every telemetry poll.
 *
 * An undocumented edge is a call between MODELED services — only the call is
 * unmodeled — so both endpoints must resolve to a real module; a name that
 * doesn't (`resolveModuleKey` → undefined) is skipped, as is a self-loop. The
 * result is sorted + deduped so equal topology yields an identity-stable array
 * (its {@link driftTopologyKey} feeds the layout cache key).
 */
export function driftTopology(
  modeledKeys: ReadonlySet<string>,
  stats: ReadonlyMap<string, EdgeStat>,
  resolveModuleKey: (normServiceName: string) => string | undefined,
): DriftPair[] {
  if (stats.size === 0) return [];
  const modeled = modeledKeys;
  const seen = new Set<string>();
  const out: DriftPair[] = [];
  for (const [key, stat] of stats) {
    if (modeled.has(key) || !(stat.perSec > 0)) continue;
    const ep = endpointsOf(stat);
    if (!ep) continue;
    const from = resolveModuleKey(ep[0]);
    const to = resolveModuleKey(ep[1]);
    if (!from || !to || from === to) continue;
    const k = `${from} ${to}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ from, to });
  }
  out.sort((a, b) => `${a.from} ${a.to}`.localeCompare(`${b.from} ${b.to}`));
  return out;
}

/** Deterministic serialization of a drift topology for the layout cache key:
 *  identical topology → identical string, independent of stat iteration order
 *  and metric values. (Pairs arrive already sorted from {@link driftTopology}.) */
export function driftTopologyKey(pairs: readonly DriftPair[]): string {
  return pairs.map((p) => `${p.from} ${p.to}`).join("|");
}

export interface Pt {
  readonly x: number;
  readonly y: number;
}

/** Center of a node rect. */
export function center(it: BoardItem): Pt {
  return { x: it.x + it.w / 2, y: it.y + it.h / 2 };
}

/**
 * The point on `it`'s border along the ray from its center toward (tx,ty).
 * Used to anchor a violation arc on the node edge instead of its center.
 */
export function rectBorderPoint(it: BoardItem, tx: number, ty: number): Pt {
  const cx = it.x + it.w / 2;
  const cy = it.y + it.h / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const sx = dx === 0 ? Infinity : it.w / 2 / Math.abs(dx);
  const sy = dy === 0 ? Infinity : it.h / 2 / Math.abs(dy);
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
}

/** Quadratic-bezier control point: the chord midpoint pushed perpendicular by
 *  `bow`× the chord length, so parallel violation arcs don't overlap the chord. */
export function arcControl(p1: Pt, p2: Pt, bow = 0.16): Pt {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular (rotate the unit chord 90°).
  return { x: mx + (-dy / len) * len * bow, y: my + (dx / len) * len * bow };
}
